/* What is promised, to whom, and where each signature stands.
 *
 * **A pending contract is not incomplete — it is waiting on a named person**, and the two signature
 * columns say which. Never a dash: "who has not signed" is the question those columns answer, and a
 * dash answers it with silence.
 *
 * **Every term treats the absence of its fact as a breach**, which is the band above the table and
 * the thing a reader most needs before reading a single row. A dataset that was never profiled does
 * not satisfy a score term by having no score.
 *
 * **The ledger leads with work rather than with a table.** Two people use this screen and they are
 * looking for different things: a steward for drafts to offer and active contracts to evaluate, an
 * approver for a signature somebody is waiting on. The tiles are derived from what this caller may
 * actually do — never from a role name, and never from a role switcher, which is a mockup device and
 * would be the one control on a governance screen that lies about who you are.
 *
 * **The state filter runs over the fetched page, and the page says when it is bounded.** The route
 * accepts `?state=`, and sending it would give the table a different source of truth from the counts
 * above it — two answers to "how many drafts are there". One fetch, one answer, and `truncated` is
 * what keeps "no contract in this state" from being a claim made at a page boundary.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  CREATE_CONTRACT_PERMISSION,
  EVALUATE_CONTRACT_PERMISSION,
  SIGN_CONTRACT_PERMISSION,
  SUBMIT_CONTRACT_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';
import type { Contract } from '@/shared/api/generated/contracts';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useContracts, useEvaluateContract } from '../api/contracts';
import {
  awaitingSignature,
  matchesFilter,
  SEVERITY_TONE,
  signoffsOf,
  stateLabelKey,
  STATE_FILTERS,
  termKeysOf,
  toneOf,
  workTilesFor,
} from '../model/contracts';
import type { StateFilter } from '../model/contracts';
import { NewContractDrawer } from './NewContractDrawer';

function ContractRow({ contract }: { readonly contract: Contract }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = (): void => {
    navigate(`/contracts/${encodeURIComponent(contract.id)}`);
  };

  return (
    <tr className="lq-ct__row" onClick={open}>
      <td>
        <strong className="lq-ct__name">{contract.name}</strong>
        {/* The terms it carries, abbreviated. A contract with none cannot exist — the server refuses
            it — so an empty list here would be a shape the API does not produce. */}
        <div className="lq-cell__secondary">
          {termKeysOf(contract)
            .map((key) => t(key))
            .join(' · ')}
        </div>
      </td>
      <td>
        <span
          className={`lq-tag lq-tag--ct-${toneOf(contract.state)}`}
          title={t(`contracts.stateHint.${contract.state}`)}
        >
          <span className="lq-tag__dot" aria-hidden="true" />
          {t(stateLabelKey(contract.state))}
        </span>
      </td>
      {signoffsOf(contract).map((signoff) => (
        <td key={signoff.side}>
          {/* Italic and warn-toned when outstanding: the absence is the finding, and it names the
              side rather than leaving a reader to work out whose turn it is. */}
          <span className={signoff.signedBy === null ? 'lq-ct__unsigned' : undefined}>
            {signoff.signedBy ?? t(`contracts.unsigned.${signoff.side}`)}
          </span>
          <div className="lq-cell__secondary lq-mono">{signoff.subject}</div>
        </td>
      ))}
      <td className="lq-nowrap">
        <span
          className={`lq-ct__sevText lq-ct__sevText--${SEVERITY_TONE[contract.breach_severity]}`}
        >
          {t(`contracts.severity.${contract.breach_severity}`)}
        </span>
        {/* Only an active contract is evaluated at all, so the severity of a draft is a plan rather
            than a consequence. */}
        <div className="lq-cell__secondary">
          {t(
            contract.state === 'active'
              ? 'contracts.severity.opens'
              : 'contracts.severity.notYet',
          )}
        </div>
      </td>
      <td className="lq-table__actions">
        <button
          type="button"
          className="lq-rowaction"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          {t('contracts.open')}
        </button>
      </td>
    </tr>
  );
}

export function ContractsLedger(): ReactElement {
  const { t } = useTranslation();
  const contracts = useContracts();
  const identity = useIdentity();
  const evaluate = useEvaluateContract();
  const [filter, setFilter] = useState<StateFilter>('all');
  const [authoring, setAuthoring] = useState(false);
  const [message, setMessage] = useState<{
    readonly titleKey: string;
    readonly bodyKey: string;
    readonly values: Record<string, string | number>;
  } | null>(null);

  const all = contracts.data?.items ?? [];
  const truncated = contracts.data?.truncated === true;
  const visible = all.filter((contract) => matchesFilter(contract, filter));
  const active = all.filter((contract) => contract.state === 'active');

  const mayCreate = can(identity.data, CREATE_CONTRACT_PERMISSION);
  const mayEvaluate = can(identity.data, EVALUATE_CONTRACT_PERMISSION);
  const tiles = workTilesFor(all, {
    submit: can(identity.data, SUBMIT_CONTRACT_PERMISSION),
    evaluate: mayEvaluate,
    sign: can(identity.data, SIGN_CONTRACT_PERMISSION),
  });

  /* One request per active contract, because there is no bulk route. Bounded by how many contracts
   * a tenant has active, which is small — and sequential rather than parallel, so a tenant with
   * thirty does not open thirty connections to its own API at once. */
  const evaluateAll = (): void => {
    setMessage(null);
    void active
      .reduce<Promise<number>>(
        (chain, contract) =>
          chain.then(async (broken) => {
            const outcome = await evaluate.mutateAsync(contract.id);
            return broken + (outcome.held ? 0 : 1);
          }),
        Promise.resolve(0),
      )
      .then((broken) => {
        setMessage({
          titleKey: 'contracts.acted.evaluatedAll',
          bodyKey: broken === 0 ? 'contracts.acted.allHeld' : 'contracts.acted.someBroke',
          values: { count: active.length, broken },
        });
      })
      .catch((failure: unknown) => {
        setMessage({
          titleKey: 'contracts.acted.evaluateFailed',
          bodyKey: 'contracts.acted.evaluateFailedBody',
          values: {
            detail: failure instanceof Error ? failure.message : String(failure),
          },
        });
      });
  };

  return (
    <div className="lq-ct lq-refscreen">
      <PageHeader
        title={t('contracts.title')}
        lede={t('contracts.intro')}
        meta={
          contracts.isSuccess ? (
            <>
              <div>{t('contracts.meta.count', { count: all.length })}</div>
              <div>{t('contracts.meta.awaiting', { count: awaitingSignature(all) })}</div>
            </>
          ) : undefined
        }
        actions={
          contracts.isSuccess ? (
            mayCreate ? (
              <button
                type="button"
                className="lq-ct__act lq-ct__act--primary"
                onClick={() => {
                  setAuthoring(true);
                }}
              >
                {t('contracts.new.open')}
              </button>
            ) : (
              /* Named and withheld rather than absent. A missing New-contract button reads as a
                 product that cannot author contracts; the chip says the control exists and is not
                 this caller's — the same distinction the detail page draws per action. */
              <span className="lq-ct__rolegated" title={t('contracts.new.roleGatedWhy')}>
                <span className="lq-ct__restrictedDot" aria-hidden="true" />
                {t('contracts.new.roleGated')}
              </span>
            )
          ) : undefined
        }
      />
      <div className="lq-pagerule" />

      {tiles.length > 0 && (
        <div className="lq-ct__work">
          {tiles.map((tile) => (
            <button
              key={tile.kind}
              type="button"
              className={`lq-ct__tile lq-ct__tile--${tile.tone}`}
              onClick={() => {
                setFilter(tile.filter);
              }}
            >
              <span className="lq-ct__tileCount lumiq-numeric">{tile.count}</span>
              <span className="lq-ct__tileLabel">{t(`contracts.work.${tile.kind}`)}</span>
              <span className="lq-ct__tileNote">
                {t(`contracts.work.${tile.kind}Note`)}
              </span>
            </button>
          ))}
        </div>
      )}

      {truncated && (
        /* The bound, stated in the filter's own terms. Without it, "no contract in this state" is a
           claim about the tenant made from a page boundary — and this listing has no cursor, so
           `truncated` is the only signal there is. */
        <div className="lq-band lq-band--warn lq-band--closing">
          <div className="lq-band__title">{t('contracts.truncated.title')}</div>
          <div className="lq-band__body">
            {t('contracts.truncated.body', { count: all.length })}
          </div>
        </div>
      )}

      {/* Before the table, because it changes how every row below is read. */}
      <div className="lq-band lq-band--info lq-band--closing">
        <div className="lq-band__title">{t('contracts.absence.title')}</div>
        <div className="lq-band__body">{t('contracts.absence.body')}</div>
      </div>

      {message !== null && (
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">
            {t(message.titleKey, message.values)}
          </strong>
          <span className="lq-flash__body">{t(message.bodyKey, message.values)}</span>
        </div>
      )}

      {contracts.isPending && <SkeletonRows rows={4} columns={6} />}

      {contracts.isError && (
        <Alert tone="error" title={t('contracts.error.title')}>
          {contracts.error.message}
        </Alert>
      )}

      {contracts.isSuccess && (
        <>
          <div className="lq-ct__filters">
            <span className="lq-ct__filterLabel">{t('contracts.filter.label')}</span>
            {STATE_FILTERS.map((each) => (
              <button
                key={each}
                type="button"
                className={
                  each === filter ? 'lq-ct__filter lq-ct__filter--on' : 'lq-ct__filter'
                }
                aria-pressed={each === filter}
                onClick={() => {
                  setFilter(each);
                }}
              >
                {t(`contracts.filter.${each}`)}
              </button>
            ))}
            <span className="lq-ct__filterGap" />
            {mayEvaluate && active.length > 0 && (
              <button
                type="button"
                className="lq-sect__ghost"
                disabled={evaluate.isPending}
                onClick={evaluateAll}
              >
                {t('contracts.evaluateAll', { count: active.length })}
              </button>
            )}
          </div>

          {visible.length === 0 ? (
            /* Three sentences rather than two. An empty estate is a fact about the tenant; an empty
               filter is a fact about the filter; an empty filter over a *bounded* page is a fact
               about the page, and reporting it as either of the others would be the claim this
               screen exists to avoid making. */
            <div className="lq-cp__none">
              <div className="lq-cp__noneTitle">
                {t(
                  filter === 'all' ? 'contracts.empty.title' : 'contracts.filtered.title',
                )}
              </div>
              <div className="lq-cp__noneBody">
                {t(
                  filter === 'all'
                    ? 'contracts.empty.body'
                    : truncated
                      ? 'contracts.filtered.bounded'
                      : 'contracts.filtered.body',
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="lq-table-scroll lq-table-scroll--framed">
                <table className="lq-table lq-table--ledger">
                  <caption className="lq-visually-hidden">
                    {t('contracts.caption')}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('contracts.column.contract')}</th>
                      <th scope="col">{t('contracts.column.state')}</th>
                      <th scope="col">{t('contracts.column.producer')}</th>
                      <th scope="col">{t('contracts.column.consumer')}</th>
                      <th scope="col">{t('contracts.column.onBreach')}</th>
                      <th scope="col" className="lq-table__actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((contract) => (
                      <ContractRow key={contract.id} contract={contract} />
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="lq-note">{t('contracts.signatureNote')}</p>
            </>
          )}
        </>
      )}

      {authoring && (
        <NewContractDrawer
          onClose={() => {
            setAuthoring(false);
          }}
          onCreated={(contract) => {
            /* The confirmation lives on the screen rather than inside the form, which unmounts —
               and it names the next step, because a draft is not yet a promise anybody has been
               asked to keep. */
            setMessage({
              titleKey: 'contracts.acted.drafted',
              bodyKey: 'contracts.acted.draftedBody',
              values: { name: contract.name },
            });
          }}
        />
      )}
    </div>
  );
}
